import { useNavigate } from 'react-router-dom'
import { Eye, Check, X, Undo2 } from 'lucide-react'
import type { ICellRendererParams } from 'ag-grid-community'
import { cn } from '@/utils/cn'
import type { ManagerSubmissionSummary } from '@/api/managerSubmissions'
import { formatUserDisplayName, formatDateShort } from '@/utils/formatters'

// --- Status Badge ---

function getStatusBadgeConfig(
  status: string,
  seenByApprover: boolean
): { label: string; className: string } {
  switch (status) {
    case 'PENDING':
      return seenByApprover
        ? {
            label: 'Submitted',
            className: 'bg-blue-100 text-blue-700 border-blue-200',
          }
        : {
            label: 'New',
            className: 'bg-rose-50 text-rose-600 border-rose-200',
          }
    case 'APPROVED':
      return {
        label: 'Approved',
        className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      }
    case 'SENT_BACK':
      return {
        label: 'Followed-Up',
        className: 'bg-amber-100 text-amber-700 border-amber-200',
      }
    case 'WITHDRAWN':
      return {
        label: 'Draft',
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      }
    case 'UNAPPROVED':
      return {
        label: 'Unapproved',
        className: 'bg-purple-100 text-purple-700 border-purple-200',
      }
    default:
      return {
        label: status,
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      }
  }
}

export function SubmissionStatusBadge(
  params: ICellRendererParams<ManagerSubmissionSummary>
) {
  if (!params.data) return null

  const { submission_status, seen_by_approver } = params.data
  const config = getStatusBadgeConfig(submission_status, seen_by_approver)

  return (
    <div className="flex items-center h-full">
      <span
        className={cn(
          'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
          config.className
        )}
      >
        {config.label}
      </span>
    </div>
  )
}

// --- Text Cell ---

export function TextCell(
  params: ICellRendererParams<ManagerSubmissionSummary>
) {
  return (
    <div className="flex items-center h-full">
      <span className="text-sm text-foreground truncate">
        {params.value ?? '-'}
      </span>
    </div>
  )
}

// --- Submitted By Cell ---

export function SubmittedByCell(
  params: ICellRendererParams<ManagerSubmissionSummary>
) {
  if (!params.data) return null

  const name = formatUserDisplayName(params.data.submitted_by)

  return (
    <div className="flex items-center h-full">
      <span className="text-sm text-foreground truncate">{name}</span>
    </div>
  )
}

// --- Date Cell ---

export function DateCell(
  params: ICellRendererParams<ManagerSubmissionSummary>
) {
  if (!params.data) return null

  return (
    <div className="flex items-center h-full">
      <span className="text-sm text-foreground">
        {formatDateShort(params.data.submitted_at)}
      </span>
    </div>
  )
}

// --- Actions Cell ---

interface ApprovalActionsCellProps extends ICellRendererParams<ManagerSubmissionSummary> {
  onApprove?: (submission: ManagerSubmissionSummary) => void
  onSendBack?: (submission: ManagerSubmissionSummary) => void
  onUnapprove?: (submission: ManagerSubmissionSummary) => void
}

export function ApprovalActionsCell(props: ApprovalActionsCellProps) {
  const navigate = useNavigate()
  const { data, onApprove, onSendBack, onUnapprove } = props

  if (!data) return null

  const isPending = data.submission_status === 'PENDING'
  const isApproved = data.submission_status === 'APPROVED'

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/approvals/${data.submission_id}`, {
      state: { submission: data },
    })
  }

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isPending && onApprove) {
      onApprove(data)
    }
  }

  const handleSendBack = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isPending && onSendBack) {
      onSendBack(data)
    }
  }

  const handleUnapprove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isApproved && onUnapprove) {
      onUnapprove(data)
    }
  }

  return (
    <div className="flex items-center justify-end gap-1 h-full">
      <button
        onClick={handleView}
        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
        title="View details"
      >
        <Eye className="h-4 w-4" />
      </button>
      {isApproved ? (
        <button
          onClick={handleUnapprove}
          className="flex h-8 w-8 items-center justify-center rounded-md cursor-pointer text-purple-600 hover:bg-purple-50 hover:text-purple-700"
          title="Unapprove"
        >
          <Undo2 className="h-4 w-4" />
        </button>
      ) : (
        <>
          <button
            onClick={handleApprove}
            disabled={!isPending}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md cursor-pointer',
              isPending
                ? 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700'
                : 'text-muted-foreground/40 cursor-not-allowed'
            )}
            title="Approve"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={handleSendBack}
            disabled={!isPending}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md cursor-pointer',
              isPending
                ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                : 'text-muted-foreground/40 cursor-not-allowed'
            )}
            title="Send back"
          >
            <X className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  )
}
